import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { ReactElement, useState } from "react";

import CollectionModal from "../../components/collection-modal/collection-modal";
import ArchiveAssets from "../../components/dialogs/asset/archive-assets";
import DownloadAssets from "../../components/dialogs/asset/download-assets";
import RemoveAssetsFromCollectionDialog from "../../components/dialogs/asset/remove-assets-from-collection";
import { Asset, Collection } from "../../generated/gql/graphql";
import useText from "../../hooks/use-text";
import { LocalizedStrings, SupportedLanguages } from "../../utils/lang";
import { styles } from "../files/filters/metashot-type-filter";

type Props = {
  targetAssets: Asset[];
  collection?: Collection;
  variant: "collection_assets" | "assets";
};

export default function AssetSelectionActions(props: Props): ReactElement {
  const { targetAssets, variant, collection } = props;

  const { strings } = useText(local);
  const [open, setOpen] = useState(false);

  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showRemoveFromCollectionDialog, setShowRemoveFromCollectionDialog] =
    useState(false);
  const [showAddToCollectionsDialog, setShowAddToCollectionsDialog] =
    useState(false);

  const disabled = targetAssets.length === 0;

  return (
    <Box sx={styles}>
      <FormControl style={{ minWidth: 150 }} size="small">
        <InputLabel className="text-[14px]">{strings.label}</InputLabel>
        <Select
          className="border-0 rounded-[15px] text-[14px] text-left"
          label="Action"
          open={open}
          onClick={() => setOpen(!open)}
        >
          <MenuItem
            disabled={disabled}
            onClick={() => setShowDownloadDialog(true)}
          >
            {strings.assetActions.download}
          </MenuItem>
          <MenuItem
            disabled={disabled}
            onClick={() => setShowArchiveDialog(true)}
          >
            {strings.assetActions.archive}
          </MenuItem>
          {variant === "collection_assets" && collection && (
            <MenuItem
              disabled={disabled}
              onClick={() => setShowRemoveFromCollectionDialog(true)}
            >
              {strings.assetActions.removeFromCollection}
            </MenuItem>
          )}
          {variant === "assets" && (
            <MenuItem
              disabled={disabled}
              onClick={() => setShowAddToCollectionsDialog(true)}
            >
              {strings.assetActions.addToCollection}
            </MenuItem>
          )}
        </Select>
        <DownloadAssets
          assets={targetAssets}
          open={showDownloadDialog}
          onClose={() => setShowDownloadDialog(false)}
        />
        <ArchiveAssets
          assets={targetAssets}
          open={showArchiveDialog}
          onClose={() => setShowArchiveDialog(false)}
        />
        {variant === "collection_assets" && collection && (
          <RemoveAssetsFromCollectionDialog
            assets={targetAssets}
            collection={collection}
            open={showRemoveFromCollectionDialog}
            onClose={() => setShowRemoveFromCollectionDialog(false)}
          />
        )}
        {variant === "assets" && (
          <CollectionModal
            selectedAssets={targetAssets}
            openModal={showAddToCollectionsDialog}
            closeModal={() => setShowAddToCollectionsDialog(false)}
          />
        )}
      </FormControl>
    </Box>
  );
}

type LocalSchema = {
  label: string;
  assetActions: {
    download: string;
    archive: string;
    removeFromCollection: string;
    addToCollection: string;
  };
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    label: "Actions",
    assetActions: {
      download: "Download selected",
      archive: "Archive selected",
      removeFromCollection: "Remove from collection",
      addToCollection: "Add to collections",
    },
  },
};
